/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd and/or Orbit Tickets Ltd. All rights reserved.
This file contains a combination of:
- Protected Intellectual Property owned by Kaleida Ltd; and
- Custom / Included Intellectual Property owned by the Customer,
and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (being any application not developed by Kaleida), whether or not such application forms part of the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, where separation between Protected Intellectual Property and Custom / Included Intellectual Property is unclear, this file shall be treated as containing Protected Intellectual Property.
*/

// IP-NOTICE: MIXED
import { Component, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { CreateUpdateBaseComponent } from "app/shared/components/create-update-base/create-update-base.component";
import { ReactiveFormHelperService } from "app/shared/helpers/reactive-form-helper.service";
import { ApiResponseSummary } from "app/shared/models/api-info.model";
import { ConfirmationDialogService } from "app/shared/services/confirmation-dialog.service";
import { passwordsMustMatch, ValidateUniqueness } from "app/shared/validation/custom-validators";
import { RegexValidationStrings } from "app/shared/validation/reg-ex-validation-constants";
import { OrganiserOnboardingService } from "../services/organiser-onboarding-service";
import { AsideService } from "app/layout/components/aside-container/aside.service";
import { OrganiserCreateModel } from "../models/organiser-create-model";
import { FormMode } from "app/shared/enumeration/form-mode";
import { Router } from "@angular/router";
import { environment } from "environments/environment";

@Component({
	selector: 'organiser-create-form',
	templateUrl: './organiser-create-form.component.html',
	styleUrls: ['./organiser-create-form.component.scss'],
	standalone: false
})

export class OrganiserCreateFormComponent extends CreateUpdateBaseComponent<OrganiserCreateModel> implements OnInit {
	@Output() inactiveOrganiserUserAccountCreated = new EventEmitter<number>();
	
	//editMode: boolean;
	hidePassword = true;

	// shortcuts for validation
	get firstNames(): AbstractControl { return this.dataForm.controls['firstNames']; }
	get lastName(): AbstractControl { return this.dataForm.controls['lastName']; }
	get email(): AbstractControl { return this.dataForm.controls['email']; }
	get phone(): AbstractControl { return this.dataForm.controls['phone']; }
	get organisation(): AbstractControl { return this.dataForm.controls['organisation']; }
	get password (): AbstractControl { return this.dataForm.controls['password']; }
	get confirmPassword (): AbstractControl { return this.dataForm.controls['confirmPassword']; }
	get recaptcha (): AbstractControl { return this.dataForm.controls['recaptcha']; }

	recaptchaResponse: string | undefined;
	get termsAndPrivacy (): AbstractControl { return this.dataForm.controls['termsAndPrivacy']; }

	get recaptchaSiteKey(): string{
		return environment.googleRecaptchaSiteKey;
	}

	constructor(
		protected fb: FormBuilder,
		private service: OrganiserOnboardingService,
		protected asideService: AsideService,
		protected formHelperService: ReactiveFormHelperService,
		protected confirmationDialogService: ConfirmationDialogService,
		//protected recaptchaV3Service: ReCaptchaV3Service,
		private router: Router,
	) {
		super(fb, asideService, formHelperService, confirmationDialogService);
		this.data = { itemId: 0, formMode: FormMode.Create };
	}

	protected createFormGroup(): FormGroup<any> {
		return this.fb.group(
			{
				firstNames: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(RegexValidationStrings.PERSON_NAME),]],
				lastName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(RegexValidationStrings.PERSON_NAME),]],
				organisation: ['', [Validators.required, Validators.maxLength(200),]],
				email: ['', [Validators.required, Validators.maxLength(254), Validators.pattern(RegexValidationStrings.EMAIL)], this.isEmailUniqueValidator()],
				phone: ['', [Validators.required, Validators.maxLength(15), Validators.pattern(RegexValidationStrings.PHONE_NUMBER)], this.isPhoneUniqueValidator()],
				password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(12), Validators.pattern(RegexValidationStrings.PASSWORD)]],
				confirmPassword: ['', [Validators.required, Validators.maxLength(12), passwordsMustMatch()]],
				recaptcha: ['', [Validators.required]],
				termsAndPrivacy: ['', [Validators.requiredTrue]],
			},
			{
			}
		);
	}

	protected populateControls(dataForm: FormGroup<any>) {
		this.dataForm.controls['firstNames'].setValue(this.viewModel.firstNames);
		this.dataForm.controls['lastName'].setValue(this.viewModel.lastName);
		this.dataForm.controls['organisation'].setValue(this.viewModel.organisation);
		this.dataForm.controls['email'].setValue(this.viewModel.email);
		this.dataForm.controls['phone'].setValue(this.viewModel.phone);
		this.dataForm.controls['termsAndPrivacy'].setValue(false);
	}

	// overidden from CreateUpdateBaseComponent base class
	protected async loadData(id: number): Promise<OrganiserCreateModel> {
		let dataModel: OrganiserCreateModel;
		dataModel = new OrganiserCreateModel();
		await this.service.getConfiguration();
		return Promise.resolve(dataModel);
	}

	protected createUpdateModel() {
		return {
			firstnames: this.firstNames.value,
			lastName: this.lastName.value,
			organisation: this.organisation.value,
			email: this.email.value,
			phone: this.phone.value,
			password: this.password.value,
			//recaptchaToken: 'NotSet',
			recaptchaToken: this.recaptcha.value,
		};
	}

	protected async doUpdate(id: number, dataToUpdate: any): Promise<ApiResponseSummary<any>> {
		throw "Not supported.";
	}

	protected async doCreate(dataToUpdate: any): Promise<any> {
		return await this.service.addNew(dataToUpdate);
	}

	onSubmitOrganiserCreate(){
		this.save();
	}

	// overidden from CreateUpdateBaseComponent base class
	// as this is an inline form, not an aside
	save() {
		if (this.formHelperService.isFormValid(this.dataForm)) {
			// Original BN for KS0009211 was recaptcha V3 but was then asked to implement V2 instead. V3 implementation is commented out.

			//this.recaptchaV3Service.execute('submitAction').subscribe((recaptchaToken: string) => {
				const updateModel = this.createUpdateModel();
				//updateModel.recaptchaToken = recaptchaToken;

				this.blockUI.start();
				this.doCreate(updateModel).then((response: any) => {
					var recordId = response.newRecordId;
					this.inactiveOrganiserUserAccountCreated.emit(recordId);
				}).finally(() => {
					this.blockUI.stop();
				});
			//});
		}
	}

	// overidden from CreateUpdateBaseComponent base class
	cancel() {
		// Makes most sense to simply reload the sign-in page, which will ensure it's in a clean state.

		var dataForm = this.dataForm;
		if (this.formHelperService.isFormDirty(dataForm)) {
			this.confirmationDialogService.openConfirmationDialogForFormLostChanges(() => {
				//this.router.navigateByUrl('/login');
				this.service.blockUi();
				location.reload();
			});
		} else {
			//this.router.navigateByUrl('/login');
			this.service.blockUi();
			location.reload();
		}
	}

	onCancel(event){
		event.preventDefault();
		this.cancel();
	}

	get termsUrl():string{
		return this.service.termsUrl;
	}

	get privacyPolicyUrl():string{
		return this.service.privacyPolicyUrl;
	}

	passwordFieldChanged(){
		if(this.confirmPassword.touched) this.confirmPassword.updateValueAndValidity();
	}

	isEmailUniqueValidator() {
		return ValidateUniqueness(() => this.service.isEmailUnique(this.data.itemId, encodeURIComponent(this.email.value)));
	}

	isPhoneUniqueValidator() {
		return ValidateUniqueness(() => this.service.isPhoneUnique(this.data.itemId, encodeURIComponent(this.phone.value)));
	}


}
